import React from "react";
import { CV } from "../components/cv";
import CarlEmilKImg from "@/assets/images/carl-emil-k.png";

export default function CVCarlEmilKjellstrand() {
  React.useEffect(() => {
    document.title = "BoldBrave CV - Carl-Emil Kjellstrand";
  }, []);

  return (
    <CV
      name="Carl-Emil Kjellstrand"
      photo={CarlEmilKImg}
      title="Senior Software Developer · Android & Kotlin"
      subtitle="20+ years building software across mobile, backend and cloud — Kotlin and Android at the core, Kotlin/Compose Multiplatform for shared Android and iOS code."
      summary="Senior developer with more than 20 years of professional experience building software across mobile, backend and cloud platforms. Core strength is Android and Kotlin development, but works comfortably across the full stack and picks up new languages, tools and ecosystems quickly — most recently using Kotlin Multiplatform and Compose Multiplatform to share code and UI across Android and iOS from a single codebase. Has made AI a central part of the daily workflow, relying on Claude Code to write, review and refactor code and to prototype with much shorter turnaround. Well-versed in Agile, Scrum, Kanban and test-driven development, with a good understanding of how to balance technical and business requirements and substantial international and cross-cultural experience. Previous roles include senior Android developer, scrum master, technical project owner and Android Competence Lead at Jayway."
      skills={{
        "Mobile": [
          "Android",
          "Kotlin",
          "Jetpack Compose",
          "Kotlin Multiplatform",
          "Compose Multiplatform",
          "Coroutines",
          "Flow",
          "J2ME",
        ],
        "Programming Languages": ["Kotlin", "Java", "Python", "Perl", "Bash"],
        "Backend & Cloud": ["Spring Boot", ".NET", "AWS", "Ansible", "BigQuery"],
        "AI-Assisted Development": ["Claude Code", "GitHub Copilot", "ChatGPT"],
        "On-Device ML": ["YOLOv8 / ONNX", "ML Kit"],
        "Ways of Working": [
          "Agile",
          "Scrum",
          "Kanban",
          "TDD",
          "Mentoring",
          "Teambuilding",
        ],
        "Tools & CI/CD": [
          "Git",
          "GitHub",
          "Gerrit",
          "Bitbucket",
          "Jenkins",
          "Azure Pipelines",
          "Jira",
          "Looker Studio",
        ],
        Languages: ["Swedish", "English", "Russian (beginner)"],
      }}
      experience={[
        {
          role: "Senior Android Developer (Consultant)",
          company: "Inter IKEA, Malmö",
          period: "2024 – 2026",
          description:
            "Android developer on the team behind IKEA's ROSS Shoppable App, building the consumer app in Kotlin and Jetpack Compose using IKEA's Skapa design system.",
          highlights: [
            "Worked in a server-driven UI architecture rendering the interface dynamically from JSON layouts, letting the team ship new features without app releases.",
            "Developed reusable Compose components and debugged complex authentication (Auth0/CIAM) and analytics (Episod) flows.",
            "Integrated with a .NET backend and contributed to CI/CD through Azure Pipelines.",
            "Worked test-driven with ownership from design to production. Kotlin, Jetpack Compose, .NET, Azure Pipelines, BigQuery, Looker Studio, Claude Code, GitHub Copilot.",
          ],
        },
        {
          role: "Parental leave",
          company: "",
          period: "2024",
          description: "",
          highlights: [],
        },
        {
          role: "Senior Android Developer (Consultant)",
          company: "Tactel, Malmö",
          period: "2019 – 2024",
          description: "Two major assignments as senior Android developer.",
          highlights: [
            "Japan Airlines' new In-Flight Entertainment system: delivered development tasks, mentored junior colleagues and proposed product and process improvements as the team grew from 6 to ~20 members by the v1.0 release.",
            "Swedbank: developed the corporate Android app for business customers, collaborating with multidisciplinary teams of 4–12 people to launch a solution that significantly improved the user experience.",
            "Kotlin, Android, Git, Jenkins, Gerrit, Bitbucket, Jira.",
          ],
        },
        {
          role: "Founder & Senior Developer",
          company: "Thinnika Tech, Malmö",
          period: "2018 – Present",
          description:
            "Founded a consulting company, both to work through and to employ other developers.",
          highlights: [
            "Long-running side product: an app for parents to keep track of their kids' money and weekly allowance, also used to stay current with the tech stack.",
            "Kotlin, Compose, Coroutines, Flow, GitHub.",
          ],
        },
        {
          role: "Senior Developer",
          company: "S9 / Sony, Malmö",
          period: "2018",
          description:
            "Backend migration of locally running code to AWS as part of a team of around ten developers.",
          highlights: ["Spring Boot, AWS, Ansible, Bash, Python, YAML, CI, TDD, Gerrit."],
        },
        {
          role: "Program Committee member",
          company: "Øredev, Malmö",
          period: "2017 – 2022",
          description:
            "Member of the Speakers Committee for a multi-track conference attracting 1300+ participants and 100+ speakers.",
          highlights: ["Found and invited speakers and reviewed submitted talks."],
        },
        {
          role: "Senior Developer & Android Competence Lead",
          company: "Jayway, Malmö",
          period: "2013 – 2018",
          description:
            "Consulting, mostly in Android assignments, and competence lead for the Android team.",
          highlights: [
            "Part-time fun fixer — organiser of team events such as pistol shooting, chilli sauce tastings and regular dinners and lunches.",
            "Java, Kotlin, Android Studio, Git, Jenkins, GitHub, Jira.",
          ],
        },
        {
          role: "Android Developer & Technical Project Owner",
          company: "Golden Gekko — Phnom Penh, Düsseldorf, Barcelona",
          period: "2010 – 2013",
          description:
            "Android development and technical project ownership for clients such as O2, Vodafone and Heathrow.",
          highlights: ["Java, Maven, Git, Jira, Scrum, CI/CD, TDD."],
        },
        {
          role: "Backend Developer",
          company: "Purple Scout AB, Malmö",
          period: "2008 – 2010",
          description:
            "Part of the team developing a front-end for Procera's PacketLogic product, alternating between scrum master and Java/Python developer.",
          highlights: [
            "Evaluation project testing video decoder performance for Axis, and syslog/MSSQL setup for the ST Ericsson tools team.",
            "Java, Python, Scrum, CI/CD, TDD, MSSQL, Syslog.",
          ],
        },
        {
          role: "Test and App Developer",
          company: "Tactel AB, Malmö",
          period: "2005 – 2008",
          description:
            "Built J2ME mobile TV apps and a framework for creating J2ME apps quickly using XML layouts and a simple scripting language for interactions.",
          highlights: ["Test tool written in Perl for automating a FOTA test suite."],
        },
      ]}
      education={[
        {
          degree: "Master of Computer Science",
          institution: "Lund University",
          year: "1998 – 2003",
        },
      ]}
      otherActivities={{
        title: "Hobby Projects",
        description:
          "Markera — a Kotlin Multiplatform / Compose Multiplatform app (Android + iOS) that automatically scores precision-shooting series from a single photo of the target: an on-device YOLOv8 ONNX model detects shot holes, ML Kit text recognition reads the printed ring digits and a total-least-squares geometric pipeline locates the target centre, robust to tilted and perspective shots. Includes a custom on-device evaluation harness rendering annotated mosaics over the dataset for rapid model iteration and a mock flavor replaying dataset images for camera-free development (github.com/carlemil/Markera). FieldShootingTimer — cross-platform timer app (Android + iOS) reading out instructions to shooters at predefined times, published on Google Play and the Apple App Store. Webshooter — an app supporting pistol shooters in Sweden during competitions, showing results and lane/patrol information. Homepage: carlemil.github.io/homepage.",
      }}
    />
  );
}
